import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from '@tanstack/react-form';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  type CreateSquadInput,
  HttpError,
  type ProjectId,
  type Squad,
  type SquadId,
  type UpdateSquadInput,
} from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { projectsRepository, squadsRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useOrgContext, useSnackbar } from '@web/ui/core';
import { fireAndForget } from '@web/ui/core/utils';

export interface SquadEditorValues {
  name: string;
  code: string;
}

const EMPTY_VALUES: SquadEditorValues = {
  name: '',
  code: '',
};

function squadToValues(squad: Squad): SquadEditorValues {
  return {
    name: squad.name,
    code: squad.code,
  };
}

/**
 * Squad editor state hook. With the flattened `/squad/$squadId/...`
 * routes, the parent project id is no longer in the URL path: edit mode
 * pulls it from `GET /orgs/$orgId/squads/$squadId`, create mode reads
 * it from the `?projectId=` search param on `/squad/new`.
 */
export function useSquadEditor() {
  const params = useParams({ strict: false });
  const search = useSearch({ strict: false });

  const squadId = params.squadId;
  const mode: 'create' | 'edit' = squadId ? 'edit' : 'create';

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { orgId, orgSlug } = useOrgContext();

  const { data: squadDetail, isLoading: isSquadLoading } = useQuery({
    queryKey: queryKeys.squads.byId(orgId, squadId as SquadId),
    queryFn: () => squadsRepository.getSquadById(orgId, squadId as SquadId),
    enabled: !!squadId,
  });

  const projectId: ProjectId | undefined =
    mode === 'edit' ? squadDetail?.projectId : search.projectId;

  const { data: project } = useQuery({
    queryKey: queryKeys.projects.detail(orgId, projectId as ProjectId),
    queryFn: () => projectsRepository.getProject(orgId, projectId as ProjectId),
    enabled: !!projectId,
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateSquadInput) =>
      squadsRepository.createSquad(orgId, projectId as ProjectId, input),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: SquadId; input: UpdateSquadInput }) =>
      squadsRepository.updateSquad(orgId, projectId as ProjectId, id, input),
  });

  const initialValues = squadDetail ? squadToValues(squadDetail) : EMPTY_VALUES;
  const isReady = mode === 'create' ? !!projectId : !isSquadLoading && !!squadDetail && !!projectId;

  function goToSquadsList(): void {
    if (!projectId) return;
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/project/$projectId/squads',
        params: { orgSlug, projectId },
      }),
    );
  }

  async function createSquad(value: SquadEditorValues): Promise<void> {
    await createMutation.mutateAsync({
      code: value.code as CreateSquadInput['code'],
      name: value.name,
    });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all }),
      queryClient.invalidateQueries({ queryKey: queryKeys.squads.all }),
      queryClient.invalidateQueries({ queryKey: queryKeys.cards.all }),
    ]);
    showSnackbar({ severity: 'success', message: t('squad.editor.snackbar.created') });
    goToSquadsList();
  }

  async function updateSquad(value: SquadEditorValues): Promise<void> {
    await updateMutation.mutateAsync({
      id: squadId as SquadId,
      input: { name: value.name },
    });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all }),
      queryClient.invalidateQueries({ queryKey: queryKeys.squads.all }),
      queryClient.invalidateQueries({ queryKey: queryKeys.cards.all }),
    ]);
    showSnackbar({ severity: 'success', message: t('squad.editor.snackbar.updated') });
    goToSquadsList();
  }

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: async ({ value }) => {
      try {
        if (mode === 'create') {
          await createSquad(value);
        } else {
          await updateSquad(value);
        }
      } catch (error) {
        // 403 → caller lost `kanban:projects:update` on the parent
        // project. Invalidate the project detail so the next render
        // (and the parent project routes) re-sync.
        if (error instanceof HttpError && error.status === 403 && projectId) {
          fireAndForget(
            queryClient.invalidateQueries({
              queryKey: queryKeys.projects.detail(orgId, projectId),
            }),
          );
        }
        showSnackbar({
          severity: 'error',
          message: error instanceof Error ? error.message : t('squad.editor.snackbar.error'),
        });
      }
    },
  });

  return {
    projectId,
    squadId,
    projectCode: project?.code,
    initialValues,
    mode,
    isReady,
    form,
  };
}
